import {
  Box,
  InputAdornment,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CheckBox,
  CircularLoader,
  EyeCloseIcon,
  EyeOpenIcon,
  Input,
} from "components";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Controller, useForm } from "react-hook-form";
import { MailBoxSection } from "../../MailBoxSection";
import { VerifiedBadge } from "../../../Badge";
import { useFetchAllEmails, useGetCustomEmailConfiguration } from "controller";
import { ToggleButton } from "components/toggle";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useVerifyCustomEmailConfiguration } from "controller";
import toast from "react-hot-toast";
import { debugConsole } from "utilz/helpers";

const CustomMailServerComponent = forwardRef(
  (
    {
      emailConfigData,
      onSelectAssigneeClicked,
    }: {
      emailConfigData?: any;
      onSelectAssigneeClicked?: () => void;
    },
    ref,
  ) => {
    const { data: existingEmailServers } = useFetchAllEmails();
    const existingEmails = existingEmailServers?.map((email: any) =>
      email?.support_email?.toLowerCase(),
    );
    const schema = yup.object({
      email: yup
        .string()
        .trim()
        .email()
        .required("Enter valid email")
        .test(
          "is-duplicate",
          "This email is already configured",
          function (value) {
            // Don't validate against itself in edit mode
            if (emailConfigData?.support_email === value) {
              return true;
            }
            // Check if email is already added
            return !existingEmails.includes(value?.toLowerCase());
          },
        ),
      imapUsername: yup.string().trim().required("Please enter username"),
      imapPassword: yup.string().trim().required("Please enter password"),
      imapHost: yup.string().trim().required("Please enter host"),
      imapPort: yup
        .number()
        .typeError("Please enter port")
        .required("Please enter port"),
      imapSecurity: yup.boolean().required().default(false),
      smtpUsername: yup
        .string()
        .trim()
        .when("diffCredentialForOutgoingMail", {
          is: true,
          then: (schema) => schema.required("Please enter username").trim(),
          otherwise: (schema) => schema.notRequired(),
        }),
      smtpPassword: yup.string().when("diffCredentialForOutgoingMail", {
        is: true,
        then: (schema) => schema.required("Please enter password").trim(),
        otherwise: (schema) => schema.notRequired(),
      }),
      smtpHost: yup.string().trim().required("Please enter host"),
      smtpPort: yup
        .number()
        .typeError("Please enter port")
        .required("Please enter port"),
      smtpSecurity: yup.boolean().required().default(true),
      diffCredentialForOutgoingMail: yup.boolean().required().default(false),
    });

    const theme = useTheme();
    const detailRef = useRef<any>();
    const [isDisabled, setIsDisabled] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isVerified, setIsVerified] = useState<{
      imap: boolean;
      smtp: boolean;
    } | null>(null);

    const { data: configData, isFetching: isConfigDataFetching } =
      useGetCustomEmailConfiguration(emailConfigData?.mapping_id);
    const {
      mutateAsync: verifyCustomEmailConfiguration,
      isLoading: isVerifying,
    } = useVerifyCustomEmailConfiguration();

    const emptyState = {
      email: "",
      imapUsername: "",
      imapPassword: "",
      imapHost: "",
      imapPort: "",
      imapSecurity: true,
      smtpUsername: "",
      smtpPassword: "",
      smtpHost: "",
      smtpPort: "",
      smtpSecurity: true,
      diffCredentialForOutgoingMail: false,
    };
    const {
      control,
      handleSubmit,
      setValue,
      getValues,
      watch,
      reset,
      formState: { errors, isDirty, dirtyFields },
    } = useForm({
      resolver: yupResolver(schema),
      defaultValues: emptyState,
    });

    const resetFormToEmptyState = () => {
      reset(emptyState);
    };

    useEffect(() => {
      if (configData && emailConfigData?.mapping_id) {
        setValue("email", configData?.emailEngineResponse?.email);
        setValue(
          "imapUsername",
          configData?.emailEngineResponse?.imap?.auth?.user,
        );
        setValue(
          "imapPassword",
          configData?.emailEngineResponse?.imap?.auth?.pass,
        );
        setValue("imapHost", configData?.emailEngineResponse?.imap?.host);
        setValue("imapPort", `${configData?.emailEngineResponse?.imap?.port}`);
        setValue("imapSecurity", configData?.emailEngineResponse?.imap?.secure);
        setValue(
          "smtpUsername",
          configData?.emailEngineResponse?.smtp?.auth?.user,
        );
        setValue(
          "smtpPassword",
          configData?.emailEngineResponse?.smtp?.auth?.pass,
        );
        setValue("smtpHost", configData?.emailEngineResponse?.smtp?.host);
        setValue("smtpPort", `${configData?.emailEngineResponse?.smtp?.port}`);
        setValue("smtpSecurity", configData?.emailEngineResponse?.smtp?.secure);

        if (
          configData?.emailEngineResponse?.imap?.auth?.user ===
            configData?.emailEngineResponse?.smtp?.auth?.user &&
          configData?.emailEngineResponse?.imap?.auth?.pass ===
            configData?.emailEngineResponse?.smtp?.auth?.pass
        ) {
          setValue("diffCredentialForOutgoingMail", false);
        } else {
          setValue("diffCredentialForOutgoingMail", true);
        }
      }
    }, [configData]);
    const diffCredentialForOutgoingMail = watch(
      "diffCredentialForOutgoingMail",
    );

    const submit = async () => {
      if (isDirty) {
        const dirtFiledsArr = Object.keys(dirtyFields);
        if (
          !dirtFiledsArr.includes("imapPassword") &&
          !dirtFiledsArr.includes("smtpPassword")
        ) {
          setValue("imapPassword", "");
          setValue("smtpPassword", "");
        }
      }
      // return;
      let result = {};
      await handleSubmit(
        async (data) => {
          try {
            if (isDirty) {
              const isVerified = await handleVerifyClick("all");
              if (isVerified && detailRef?.current) {
                const boardData = await detailRef?.current?.submit();
                result = {
                  board_id: boardData?.board,
                  email: data.email.trim(),
                  imap: {
                    auth: {
                      user: data.imapUsername.trim(),
                      pass: data.imapPassword,
                    },
                    host: data.imapHost.trim(),
                    port: Number(data.imapPort) || 0,
                    secure: data.imapSecurity,
                  },
                  smtp: {
                    auth: {
                      user: data.diffCredentialForOutgoingMail
                        ? data.smtpUsername.trim()
                        : data.imapUsername.trim(),
                      pass: data.diffCredentialForOutgoingMail
                        ? data.smtpPassword
                        : data.imapPassword,
                    },
                    host: data.smtpHost.trim(),
                    port: Number(data.smtpPort) || 0,
                    secure: data.smtpSecurity,
                  },
                  ...boardData,
                };
              }
            } else if (detailRef?.current) {
              const boardData = await detailRef?.current?.submit();
              result = {
                board_id: boardData?.board,
                ...boardData,
              };
            }
          } catch (error) {
            debugConsole(error);
          }
        },
        (err) => {
          debugConsole(err);
        },
      )();
      return result;
    };

    const handleVerifyClick = async (type: "imap" | "smtp" | "all") => {
      try {
        const isCredsDifferent = getValues("diffCredentialForOutgoingMail");
        const data = {
          imap: {
            auth: {
              user: getValues("imapUsername").trim(),
              pass: getValues("imapPassword"),
            },
            host: getValues("imapHost").trim(),
            port: Number(getValues("imapPort")) || 0,
            secure: getValues("imapSecurity"),
          },

          smtp: {
            auth: {
              user: isCredsDifferent
                ? getValues("smtpUsername").trim()
                : getValues("imapUsername").trim(),
              pass: isCredsDifferent
                ? getValues("smtpPassword")
                : getValues("imapPassword"),
            },
            host: getValues("smtpHost").trim(),
            port: Number(getValues("smtpPort")) || 0,
            secure: getValues("smtpSecurity"),
          },
        };

        const verificationData = await verifyCustomEmailConfiguration(data);

        if (
          verificationData?.imap?.success &&
          verificationData?.smtp?.success
        ) {
          return true;
        } else {
          setIsVerified({
            imap: verificationData?.imap?.success,
            smtp: verificationData?.smtp?.success,
          });
          toast.error("Unable to verify email configuration");
        }

        return false;
      } catch (error) {
        toast.error("Failed to verify email configuration");
      }
    };

    const handleDifferentCredentialToggle = (value: boolean) => {
      if (value) {
        setValue("smtpUsername", getValues("imapUsername").trim());
        setValue("smtpPassword", getValues("imapPassword"));
      }
    };

    useImperativeHandle(ref, () => ({ submit, resetFormToEmptyState }));

    return (
      <Stack>
        {isConfigDataFetching && emailConfigData?.mapping_id ? (
          <Stack
            sx={{
              height: "100px",
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularLoader />
          </Stack>
        ) : (
          <Stack gap={"16px"} pt={"8px"}>
            <MailBoxSection
              onSelectAssigneeClicked={onSelectAssigneeClicked}
              selectedResources={[]}
              onRemoveAssigneeClicked={(mappingId: string) => {}}
              ref={detailRef}
              board={configData?.board}
              displayFrom={configData?.display_from}
              customDisplayName={configData?.custom_display_name}
            />
            <Stack
              sx={{
                bgcolor: "#FFFF",
                p: "24px",
                gap: "24px",
                borderRadius: "8px",
                border: `1px solid ${theme.palette.neutral["015"]}`,
              }}
            >
              <Stack gap="16px">
                <Typography variant="h6">Support Email</Typography>
                <Input
                  label="Support email"
                  placeholder="Enter the email to authenticate"
                  name="email"
                  required
                  control={control}
                  fullWidth
                  error={!!errors.email}
                  errorMessage={errors.email?.message}
                  inputProps={{ maxLength: 100 }}
                  disabled={isDisabled}
                />
              </Stack>

              {/* incoming mail settings */}
              <Stack
                gap="24px"
                sx={{
                  // marginTop: "24px",
                  bgcolor: "#FFFF",
                  p: "16px",
                  gap: "24px",
                  borderRadius: "8px",
                  border:
                    isVerified?.imap === false
                      ? `1px solid ${theme.palette.error.main}`
                      : `1px solid ${theme.palette.neutral["015"]}`,
                }}
              >
                <Stack gap="8px">
                  <Typography variant="subtitle1">
                    Incoming Mail Settings
                  </Typography>
                </Stack>
                <Stack gap="24px">
                  <Input
                    label="Username"
                    placeholder="Enter the Username to authenticate"
                    name="imapUsername"
                    required
                    control={control}
                    fullWidth
                    error={!!errors.imapUsername}
                    errorMessage={errors.imapUsername?.message}
                    inputProps={{ maxLength: 100 }}
                    disabled={isDisabled}
                  />
                  <Input
                    label="Password"
                    placeholder="Enter the Password to authenticate"
                    name="imapPassword"
                    required
                    control={control}
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    error={!!errors.imapPassword}
                    errorMessage={errors.imapPassword?.message}
                    inputProps={{ maxLength: 100 }}
                    disabled={isDisabled}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Box
                            sx={{ cursor: "pointer" }}
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {!showPassword ? (
                              <EyeOpenIcon fontSize="small" />
                            ) : (
                              <EyeCloseIcon fontSize="small" />
                            )}
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Stack direction={"row"} gap="16px">
                    <Stack flex={1}>
                      <Input
                        label="IMAP Host"
                        placeholder="Enter the IMAP Server address here"
                        required
                        name="imapHost"
                        control={control}
                        fullWidth
                        error={!!errors.imapHost}
                        errorMessage={errors.imapHost?.message}
                        inputProps={{ maxLength: 100 }}
                        disabled={isDisabled}
                      />
                    </Stack>
                    <Stack flex={1}>
                      <Input
                        label="IMAP Port"
                        placeholder="Enter IMAP Port here"
                        name="imapPort"
                        required
                        allowOnlyNumbers
                        control={control}
                        fullWidth
                        error={!!errors.imapPort}
                        errorMessage={errors.imapPort?.message}
                        inputProps={{ maxLength: 100 }}
                        disabled={isDisabled}
                      />
                    </Stack>
                  </Stack>
                </Stack>

                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Stack direction={"row"} gap={"8px"}>
                    <Controller
                      name="imapSecurity"
                      control={control}
                      render={({ field }) => (
                        <CheckBox
                          disabled={isDisabled}
                          checked={field.value}
                          onClick={() => {
                            field.onChange(!field.value);
                          }}
                        />
                      )}
                    />
                    <Typography variant="body-medium">Use SSL</Typography>
                  </Stack>
                  {isVerified?.imap === false ? (
                    <VerifiedBadge type="error" />
                  ) : (
                    <></>
                  )}
                </Stack>
              </Stack>

              {/* Out going mail settings */}
              <Stack
                gap="24px"
                sx={{
                  // marginTop: "24px",
                  bgcolor: "#FFFF",
                  p: "16px",
                  gap: "24px",
                  borderRadius: "8px",
                  border:
                    isVerified?.imap === false
                      ? `1px solid ${theme.palette.error.main}`
                      : `1px solid ${theme.palette.neutral["015"]}`,
                }}
              >
                <Typography variant="subtitle1">
                  Outgoing Mail Settings
                </Typography>
                <Stack direction={"row"} gap={"8px"}>
                  <Controller
                    name="diffCredentialForOutgoingMail"
                    control={control}
                    render={({ field }) => (
                      <ToggleButton
                        isDisabled={false}
                        name={"diffCredentialForOutgoingMail"}
                        // control={control}
                        isChecked={field.value}
                        onChange={(value) => {
                          field.onChange(value);
                          handleDifferentCredentialToggle(value);
                        }}
                      />
                    )}
                  />
                  <Typography variant="body-large">
                    Use different credentials for outgoing mail
                  </Typography>
                </Stack>
                {diffCredentialForOutgoingMail && (
                  <Stack gap="24px">
                    <Input
                      label="Username"
                      placeholder="Enter the Username to authenticate"
                      name="smtpUsername"
                      required
                      control={control}
                      fullWidth
                      error={!!errors.smtpUsername}
                      errorMessage={errors.smtpUsername?.message}
                      inputProps={{ maxLength: 100 }}
                      disabled={isDisabled}
                    />
                    <Input
                      label="Password"
                      placeholder="Enter the Password to authenticate"
                      name="smtpPassword"
                      required
                      type={showPassword ? "text" : "password"}
                      control={control}
                      fullWidth
                      error={!!errors.smtpPassword}
                      errorMessage={errors.smtpPassword?.message}
                      inputProps={{ maxLength: 100 }}
                      disabled={isDisabled}
                    />
                  </Stack>
                )}
                <Stack gap="24px">
                  <Input
                    label="SMTP Host"
                    placeholder="Enter the SMTP Server address here"
                    name="smtpHost"
                    required
                    control={control}
                    fullWidth
                    error={!!errors.smtpHost}
                    errorMessage={errors.smtpHost?.message}
                    inputProps={{ maxLength: 100 }}
                    disabled={isDisabled}
                  />
                  <Input
                    label="SMTP Port"
                    placeholder="Enter the SMTP Server address here"
                    name="smtpPort"
                    required
                    allowOnlyNumbers
                    control={control}
                    fullWidth
                    error={!!errors.smtpPort}
                    errorMessage={errors.smtpPort?.message}
                    inputProps={{ maxLength: 100 }}
                    disabled={isDisabled}
                  />
                </Stack>

                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Stack direction={"row"} gap={"8px"}>
                    <Controller
                      name="smtpSecurity"
                      control={control}
                      render={({ field }) => (
                        <CheckBox
                          disabled={isDisabled}
                          checked={field.value}
                          onClick={() => {
                            field.onChange(!field.value);
                          }}
                        />
                      )}
                    />
                    <Typography variant="body-medium">Use SSL</Typography>
                  </Stack>
                  {isVerified?.smtp === false ? (
                    <VerifiedBadge type="error" />
                  ) : (
                    <></>
                  )}
                </Stack>
              </Stack>
            </Stack>
            {/* <Button onClick={submit}>Submit</Button> */}
            {/* <Stack
        gap={"12px"}
        sx={{
          // marginTop: "24px",
          bgcolor: "#FFFF",
          p: "24px",
          gap: "8px",
          borderRadius: "8px",
        }}
      >
        <DummyMailBadge
          variant="success"
          content="Dummy Content for Incoming Mail"
        />

        <DummyMailBadge
          variant="error"
          content="Dummy Content for Outgoing Mail"
        />
      </Stack> */}
          </Stack>
        )}
      </Stack>
    );
  },
);

export default CustomMailServerComponent;
